import { gql, useLazyQuery } from "@apollo/client";
import { useCallback, useState } from "react";
import { COMPANY_SEARCH_CLIENT } from "@/utils/consts";
import {
  fetchScoringModelConfig,
  type ScoringModelConfig,
} from "@/apis/argo/scoringModels";

/**
 * Realtime V2 (argo model) scoring with an optional rule-level `explain`
 * breakdown. Proxies to hermes' scoring engine via cs-api; consumed exactly like
 * `opportunityGeneratorSearch` (same Apollo client, `/graphql`, COMPANY_SEARCH
 * context). `scope`/`config` are the `JsonInput` scalar — Apollo sends them as
 * inline JSON objects. `data` is opaque raw JSON (nested keys are the client's
 * config keys), so it's typed here rather than via codegen.
 *
 * See company-search-api#1298 / hermes-api scoring-explainability design.
 */
const AI_SCORING_SCORE = gql`
  query AiScoringScore(
    $scope: JsonInput!
    $config: JsonInput!
    $explain: Boolean
    $limit: Int
    $tenantId: String
  ) {
    aiScoringScore(
      scope: $scope
      config: $config
      explain: $explain
      limit: $limit
      tenantId: $tenantId
    ) {
      count
      data
    }
  }
`;

/** One matched rule inside a sub-dimension — the readable "why" (when
 * `explain: true`). `label` is display-ready; `points` is its contribution. */
export interface ScoreReason {
  /** Stable rule index (position in the sub-dimension's `rules`). */
  rule?: number;
  label: string;
  points: number;
}

export interface ScoreSubDimension {
  score: number;
  max_points?: number;
  raw?: number;
  reasons?: ScoreReason[];
}

export interface ScoreDimension {
  score: number;
  aggregate?: "sum" | "avg" | "max";
  max_points?: number;
  raw?: number;
  sub_dimensions?: Record<string, ScoreSubDimension>;
}

export interface ScorePillar {
  score: number;
  normalization?: { raw: number; max_points: number };
  dimensions?: Record<string, ScoreDimension>;
}

/** A disqualifier rule that fired on an account — present in `disqualified_by`
 * when the row is DQ'd. `label` is display-ready; a `geography` type carries
 * country codes we expand to names. */
export interface DisqualifierReason {
  pillar?: string;
  index?: number;
  type?: string;
  label?: string;
}

/** One scored account row (`aiScoringScore.data[i]`). `pillars` is present only
 * when the request set `explain: true`. */
export interface ScoredAccount {
  hg_id: string;
  url?: string;
  composite?: number;
  tier?: string;
  pillars?: Record<string, ScorePillar>;
  disqualified_by?: DisqualifierReason[];
}

interface AiScoringScoreResponse {
  aiScoringScore: {
    count: number;
    data: ScoredAccount[];
  };
}

interface AiScoringScoreVariables {
  scope: { hg_ids: string[] };
  config: ScoringModelConfig;
  explain?: boolean;
  limit?: number;
  tenantId?: string;
}

/**
 * A `limit` must be sent: hermes currently 422s on a null limit
 * ("null value where integer expected"). The drill-down scope is a single
 * company, so 1 suffices. (Remove/relax once cs-api defaults it — the limit is
 * being made optional backend-side.)
 */
const BREAKDOWN_LIMIT = 1;

/**
 * Fetches the rule-level breakdown for a single company against a single V2
 * model, for the score-drawer drill-down. Two steps, surfaced as one:
 *   1. fetch the model's full config from argo (`fetchScoringModelConfig`),
 *   2. score that company with `explain: true` via cs-api.
 *
 * `run({ hgId, modelId, tenantId })` triggers the fetch; `result` is the single
 * scored account (or null); `loading`/`error` cover both steps.
 */
export function useModelScoreBreakdown() {
  const [runScore, { data, loading: scoreLoading, error: scoreError }] =
    useLazyQuery<AiScoringScoreResponse, AiScoringScoreVariables>(
      AI_SCORING_SCORE,
      {
        context: { clientName: COMPANY_SEARCH_CLIENT },
        fetchPolicy: "no-cache",
      }
    );

  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<Error | null>(null);

  const run = useCallback(
    async ({
      hgId,
      modelId,
      tenantId,
    }: {
      hgId: string;
      modelId: string;
      tenantId: string;
    }) => {
      setConfigError(null);
      setConfigLoading(true);
      try {
        const config = await fetchScoringModelConfig(tenantId, modelId);
        setConfigLoading(false);
        if (!config) {
          setConfigError(new Error("This model has no published config."));
          return;
        }
        void runScore({
          variables: {
            scope: { hg_ids: [hgId] },
            config,
            explain: true,
            limit: BREAKDOWN_LIMIT,
            tenantId,
          },
        });
      } catch {
        setConfigLoading(false);
        setConfigError(new Error("Failed to load the scoring model config."));
      }
    },
    [runScore]
  );

  const result = data?.aiScoringScore?.data?.[0] ?? null;

  return {
    run,
    result,
    loading: configLoading || scoreLoading,
    error: configError ?? scoreError ?? null,
  };
}
